// src/utils/scoringUtils.ts

import { ScoringRubricItem } from '../types';

/**
 * Parse and validate scoring logic JSON string
 * @param scoringLogicJson - JSON string from backend
 * @returns Validated ScoringRubricItem array or null if invalid
 */
export function parseScoringLogic(scoringLogicJson: string | null): ScoringRubricItem[] | null {
  if (!scoringLogicJson) {
    return null;
  }

  try {
    const parsed = JSON.parse(scoringLogicJson);
    
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return null;
    }

    // Validate each item has required ScoringRubricItem fields (including backward compatibility)
    const isValidScoringRubric = parsed.every(item => 
      item &&
      typeof item.scoringCategory === 'string' &&
      typeof item.totalPoints === 'number' &&
      typeof item.pointsScored === 'number' &&
      typeof item.evaluationNotes === 'string'
      // NOTE: messages and categorySpecificInstructions are optional for backward compatibility
    );

    if (!isValidScoringRubric) {
      console.warn('⚠️ Invalid scoring rubric structure detected:', parsed);
      return null;
    }

    // Ensure backward compatibility by adding default values for new fields
    const normalizedRubric = parsed.map(item => ({
      scoringCategory: item.scoringCategory,
      totalPoints: item.totalPoints,
      pointsScored: item.pointsScored,
      evaluationNotes: item.evaluationNotes,
      messages: item.messages || [], // NEW: Default to empty array
      categorySpecificInstructions: item.categorySpecificInstructions || "" // NEW: Default to empty string
    }));

    return normalizedRubric as ScoringRubricItem[];
  } catch (parseError) {
    console.error('❌ Failed to parse scoring logic JSON:', parseError);
    console.warn('Raw scoring_logic value:', scoringLogicJson);
    return null;
  }
}

/**
 * Safely get scoring statistics from a scoring logic JSON string
 * @param scoringLogicJson - JSON string from backend
 * @returns Basic scoring statistics or null
 */
export function getScoringStatistics(scoringLogicJson: string | null): {
  totalItems: number;
  totalPoints: number;
  currentPoints: number;
  completionPercentage: number;
  // NEW: Enhanced statistics
  totalMessages: number;
  categoriesWithMessages: number;
  categoriesWithInstructions: number;
  averageMessagesPerCategory: number;
} | null {
  const rubric = parseScoringLogic(scoringLogicJson);
  
  if (!rubric) {
    return null;
  }

  const totalItems = rubric.length;
  const totalPoints = rubric.reduce((sum, item) => sum + item.totalPoints, 0);
  const currentPoints = rubric.reduce((sum, item) => sum + item.pointsScored, 0);
  const completionPercentage = totalPoints > 0 ? Math.round((currentPoints / totalPoints) * 100) : 0;

  // NEW: Enhanced statistics
  const totalMessages = rubric.reduce((sum, item) => sum + (item.messages?.length || 0), 0);
  const categoriesWithMessages = rubric.filter(item => item.messages && item.messages.length > 0).length;
  const categoriesWithInstructions = rubric.filter(item => item.categorySpecificInstructions && item.categorySpecificInstructions.length > 0).length;
  const averageMessagesPerCategory = totalItems > 0 ? Math.round((totalMessages / totalItems) * 10) / 10 : 0;

  return {
    totalItems,
    totalPoints,
    currentPoints,
    completionPercentage,
    totalMessages,
    categoriesWithMessages,
    categoriesWithInstructions,
    averageMessagesPerCategory
  };
}

/**
 * Check if a case has a valid scoring rubric
 * @param scoringLogicJson - JSON string from backend
 * @returns True if valid scoring rubric exists
 */
export function hasScoringRubric(scoringLogicJson: string | null): boolean {
  return parseScoringLogic(scoringLogicJson) !== null;
}

/**
 * NEW: Get rubric analysis for UI display
 * @param rubric - ScoringRubricItem array
 * @returns Analysis data for each category
 */
export function analyzeRubricCategories(rubric: ScoringRubricItem[]): Array<{
  category: string;
  progress: number;
  hasInstructions: boolean;
  messageCount: number;
  isCompleted: boolean;
  recentEvidence: string[];
  pointsEarned: number;
  pointsPossible: number;
}> {
  return rubric.map(item => ({
    category: item.scoringCategory,
    progress: item.totalPoints > 0 ? Math.round((item.pointsScored / item.totalPoints) * 100) : 0,
    hasInstructions: !!(item.categorySpecificInstructions && item.categorySpecificInstructions.length > 0),
    messageCount: item.messages?.length || 0,
    isCompleted: item.pointsScored >= item.totalPoints,
    recentEvidence: item.messages?.slice(-3) || [], // Last 3 messages
    pointsEarned: item.pointsScored,
    pointsPossible: item.totalPoints
  }));
}

/**
 * NEW: Extract evidence summary for a category
 * @param item - ScoringRubricItem
 * @returns Human-readable evidence summary
 */
export function getEvidenceSummary(item: ScoringRubricItem): string {
  if (!item.messages || item.messages.length === 0) {
    return "No evidence recorded yet";
  }

  const messageCount = item.messages.length;
  const recentMessages = item.messages.slice(-2);
  
  if (messageCount === 1) {
    return `1 piece of evidence: ${recentMessages[0]}`;
  } else if (messageCount <= 3) {
    return `${messageCount} pieces of evidence including: ${recentMessages.join('; ')}`;
  } else {
    return `${messageCount} pieces of evidence, most recent: ${recentMessages.join('; ')}`;
  }
}

/**
 * NEW: Check if a rubric has enhanced features (messages/instructions)
 * @param rubric - ScoringRubricItem array
 * @returns True if rubric uses new enhanced features
 */
export function hasEnhancedFeatures(rubric: ScoringRubricItem[]): boolean {
  return rubric.some(item => 
    (item.messages && item.messages.length > 0) || 
    (item.categorySpecificInstructions && item.categorySpecificInstructions.length > 0)
  );
}

/**
 * NEW: Validate rubric item structure (including new fields)
 * @param item - Raw rubric item object
 * @returns True if valid ScoringRubricItem
 */
export function isValidRubricItem(item: any): item is ScoringRubricItem {
  return (
    item &&
    typeof item.scoringCategory === 'string' &&
    typeof item.totalPoints === 'number' &&
    typeof item.pointsScored === 'number' &&
    typeof item.evaluationNotes === 'string' &&
    (!item.messages || Array.isArray(item.messages)) &&
    (!item.categorySpecificInstructions || typeof item.categorySpecificInstructions === 'string')
  );
}

/**
 * NEW: Create a default rubric item with proper field initialization
 * @param category - Category name
 * @param totalPoints - Total points possible
 * @returns Properly initialized ScoringRubricItem
 */
export function createDefaultRubricItem(category: string, totalPoints: number): ScoringRubricItem {
  return {
    scoringCategory: category,
    totalPoints,
    pointsScored: 0,
    evaluationNotes: "",
    messages: [],
    categorySpecificInstructions: ""
  };
}